import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listMefo, getMefoManufacturers } from "../lib/api.js";
import type { MefoListItem } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, HashIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * MEFO katalog — externí distributor (mefo.cz XML feed).
 *
 * Karty zobrazují: obrázek z MEFO, název, výrobce, cenu s DPH, dodací lhůtu.
 * Klik na kartu otevře externí stránku mefo.cz v novém tabu (ne interní detail,
 * protože MEFO má vlastní e-shop s kompletními informacemi).
 */
export function MefoPage(): JSX.Element {
  const [manufacturers, setManufacturers] = useState<
    Array<{ name: string; count: number }> | null
  >(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<MefoListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset offset při změně filtru
  useEffect(() => {
    setOffset(0);
  }, [selectedManufacturer, debouncedSearch]);

  // Fetch manufacturers once
  useEffect(() => {
    getMefoManufacturers()
      .then((d) => setManufacturers(d.manufacturers))
      .catch(() => setManufacturers([]));
  }, []);

  // Fetch products
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMefo({
      manufacturer: selectedManufacturer ?? undefined,
      search: debouncedSearch || undefined,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedManufacturer, debouncedSearch, offset]);

  const topManufacturers = useMemo(
    () => (manufacturers ?? []).slice(0, 8),
    [manufacturers],
  );
  const restManufacturers = useMemo(
    () => (manufacturers ?? []).slice(8),
    [manufacturers],
  );

  if (!manufacturers) return <LoadingBlock label="Načítám výrobce…" />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          MEFO
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Externí katalog distributora MEFO — Acerbis, Renthal, Twin Air a další.
          Data synchronizována z <code className="text-xs bg-zinc-100 px-1 rounded">mefo.cz</code>.
        </p>
      </header>

      {/* Manufacturer tabs */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        <button
          type="button"
          onClick={() => setSelectedManufacturer(null)}
          className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
            selectedManufacturer === null
              ? "bg-ktm-orange text-white border-ktm-orange"
              : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
          }`}
        >
          <span className="font-medium">Vše</span>
          <span
            className={`ml-2 text-xs ${selectedManufacturer === null ? "text-white/80" : "text-zinc-400"}`}
          >
            {(manufacturers.reduce((s, m) => s + m.count, 0)).toLocaleString("cs-CZ")}
          </span>
        </button>
        {topManufacturers.map((m) => {
          const isActive = selectedManufacturer === m.name;
          return (
            <button
              key={m.name}
              type="button"
              onClick={() => setSelectedManufacturer(m.name)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
                isActive
                  ? "bg-ktm-orange text-white border-ktm-orange"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <span className="font-medium">{m.name}</span>
              <span className={`ml-2 text-xs ${isActive ? "text-white/80" : "text-zinc-400"}`}>
                {m.count.toLocaleString("cs-CZ")}
              </span>
            </button>
          );
        })}
        {restManufacturers.length > 0 && (
          <select
            value={
              selectedManufacturer && !topManufacturers.some((m) => m.name === selectedManufacturer)
                ? selectedManufacturer
                : ""
            }
            onChange={(e) => setSelectedManufacturer(e.target.value || null)}
            className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white"
          >
            <option value="">Další výrobci…</option>
            {restManufacturers.map((m) => (
              <option key={m.name} value={m.name}>
                {m.name} ({m.count})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat číslo, název, EAN nebo výrobce…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-80"
          />
        </div>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {/* Grid */}
      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné výsledky"
          description="Upravte filtr nebo hledání."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <MefoCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function MefoCard({ item }: { item: MefoListItem }): JSX.Element {
  const inStock = item.deliveryDate === 0;
  const shortDelivery = item.deliveryDate != null && item.deliveryDate > 0 && item.deliveryDate <= 3;
  const deliveryLabel =
    item.deliveryDate == null
      ? null
      : item.deliveryDate === 0
        ? "Skladem"
        : `${item.deliveryDate} ${item.deliveryDate === 1 ? "den" : item.deliveryDate < 5 ? "dny" : "dnů"}`;

  return (
    <Link
      to={`/mefo/${encodeURIComponent(item.itemId)}`}
      className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.imgUrl ? (
          <img
            src={item.imgUrl}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        {deliveryLabel && (
          <div className="absolute bottom-2 left-2">
            <Badge tone={inStock ? "green" : shortDelivery ? "amber" : "neutral"}>
              {deliveryLabel}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono flex items-center gap-1.5">
          <span>{item.kodZbozi}</span>
          {item.manufacturer && (
            <>
              <span className="text-zinc-300">·</span>
              <span className="truncate">{item.manufacturer}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            {item.priceGross != null ? (
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  {item.priceGross.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč
                </div>
                {item.priceNet != null && (
                  <div className="text-[11px] text-zinc-500">
                    bez DPH {item.priceNet.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč
                  </div>
                )}
              </div>
            ) : (
              <span className="text-xs text-zinc-400">Bez ceny</span>
            )}
          </div>
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}
